/**
 * Closed UI contract for the flagship value-governance control panel.
 *
 * The contract deliberately carries record references and posture only. Dollar
 * values stay on their existing governed numeric claims; this panel must never
 * become a second arithmetic or receipt authority.
 */

export const VALUE_GOVERNANCE_PANEL_SCHEMA_VERSION = 'value_governance_panel_v1' as const;

export const VALUE_GOVERNANCE_PANEL_HEADLINE =
  'Every dollar needs a source, a calculation, an accountable owner, a double-count check, and a realized P&L outcome' as const;

export const VALUE_GOVERNANCE_PANEL_TAKEAWAY =
  'A dollar enters the control plan only when all five records are connected. Anything still missing a record is shown here as not yet supported, never hidden.' as const;

export const VALUE_GOVERNANCE_REALIZATION_RULE =
  'Narrative never creates value. The source, the calculation, the accountable owner, the double-count check, and the realized P&L outcome must agree before a dollar is certified.' as const;

/** Closed executive labels for the governed candidate-lever roster.
 *
 * RATCHET 2026-08-05 (#4449): 29 -> 26. Three entries removed — mode_mix_shift,
 * revenue_mix_shift, growth_cross_sell — because their modules were RETIRED and
 * their trees deleted. This map is paired 1:1 with CANDIDATE_LEVER_ROSTER (the
 * flagship value-governance suite asserts exactly that), so leaving them here
 * would have left a CLOSED registry advertising levers the product can no longer
 * offer. working_capital and capex_discipline never appeared here: they are
 * cash-track levers and never entered the operating-income panel.
 */
export const VALUE_GOVERNANCE_CATEGORY_LABELS = {
  transportation: 'Purchased transportation',
  network_optimization: 'Network design',
  capacity_utilization: 'Capacity utilization',
  fuel_efficiency: 'Fuel and accessorial leakage',
  pricing_uplift: 'Price realization',
  discount_leakage: 'Quote-to-invoice leakage',
  churn_reduction: 'Customer retention',
  cost_to_serve: 'Cost to serve',
  segment_cleanup: 'Segment margin cleanup',
  productivity_lean_ai: 'Productivity / Lean-AI',
  warehouse_productivity: 'Warehouse productivity',
  automation: 'Operating process automation',
  risk_claims: 'Risk & claims',
  sga_spend: 'SG&A optimization',
  travel_and_expense: 'Travel and expense',
  indirect_procurement: 'Indirect procurement',
  professional_fees: 'Professional fees',
  tech_software: 'Technology and software',
  outside_contractors: 'Outside contractors',
  labor_arbitrage: 'Location and shoring',
  real_estate_footprint: 'Real estate footprint',
  shared_services: 'Shared services',
  marketing_spend: 'Marketing spend',
  org_cost: 'Org analysis & delayering',
  span_of_control_rightsizing: 'Span-of-control right-sizing',
  workforce_reduction: 'Workforce reduction',
} as const;

export type ValueGovernanceCategoryId = keyof typeof VALUE_GOVERNANCE_CATEGORY_LABELS;

export const VALUE_GOVERNANCE_CATEGORY_IDS = Object.freeze(
  Object.keys(VALUE_GOVERNANCE_CATEGORY_LABELS) as ValueGovernanceCategoryId[],
);

export const VALUE_GOVERNANCE_FACET_IDS = [
  'source',
  'formula',
  'owner',
  'overlap_receipt',
  'realized_p_and_l',
] as const;

export type ValueGovernanceFacetId = (typeof VALUE_GOVERNANCE_FACET_IDS)[number];

export const VALUE_GOVERNANCE_FACET_STATUSES = ['live', 'modeled', 'not_yet_armed'] as const;

export type ValueGovernanceFacetStatus = (typeof VALUE_GOVERNANCE_FACET_STATUSES)[number];

export const VALUE_GOVERNANCE_LAYER_STATUSES = [
  'armed',
  'partially_armed',
  'not_yet_armed',
] as const;

export type ValueGovernanceLayerStatus = (typeof VALUE_GOVERNANCE_LAYER_STATUSES)[number];

export const VALUE_GOVERNANCE_RECORD_TYPES = [
  'control_layer_category',
  'governed_source',
  'formula_execution',
  'owner_approval',
  'overlap_disposition',
  'realized_p_and_l_outcome',
] as const;

export type ValueGovernanceRecordType = (typeof VALUE_GOVERNANCE_RECORD_TYPES)[number];

/** Closed presentation labels for record types whose identity stays private. */
export const VALUE_GOVERNANCE_RECORD_LABELS = {
  governed_source: 'Approved source record',
  formula_execution: 'Approved calculation run',
  owner_approval: 'Accountable owner approval',
  overlap_disposition: 'Double-count check',
  realized_p_and_l_outcome: 'Observed realized P&L outcome',
} as const satisfies Record<Exclude<ValueGovernanceRecordType, 'control_layer_category'>, string>;

export type ValueGovernanceRecordBinding = Readonly<{
  recordType: ValueGovernanceRecordType;
  /** Executive-safe record identity; raw runtime and database ids never cross this contract. */
  label: string;
  asOf: string | null;
}>;

export type ValueGovernanceFacet = Readonly<{
  facetId: ValueGovernanceFacetId;
  label: string;
  status: ValueGovernanceFacetStatus;
  /** Reader-facing words for `status`; never derived by upper-casing the enum. */
  statusLabel: string;
  summary: string;
  recordBindings: readonly ValueGovernanceRecordBinding[];
}>;

export type ValueGovernanceCategory = Readonly<{
  /** Live lever identity from the candidate stack, never an authored row. */
  categoryId: string;
  label: string;
  facets: readonly ValueGovernanceFacet[];
}>;

export const VALUE_GOVERNANCE_CONTROL_LAYER_IDS = [
  'initiative_stand_up',
  'value_certification',
  'customer_carrier_guardrails',
  'transformation_steering',
  'board_value_review',
] as const;

export type ValueGovernanceControlLayerId = (typeof VALUE_GOVERNANCE_CONTROL_LAYER_IDS)[number];

export type ValueGovernanceControlLayerDefinition = Readonly<{
  layerId: ValueGovernanceControlLayerId;
  label: string;
  cadence: string;
  owner: string;
  requiredOutput: string;
  requiredFacets: readonly ValueGovernanceFacetId[];
}>;

export const VALUE_GOVERNANCE_FACETS = [
  { facetId: 'source', label: 'Source' },
  { facetId: 'formula', label: 'Calculation' },
  { facetId: 'owner', label: 'Owner' },
  { facetId: 'overlap_receipt', label: 'Double-count check' },
  { facetId: 'realized_p_and_l', label: 'Realized P&L' },
] as const satisfies readonly Readonly<{
  facetId: ValueGovernanceFacetId;
  label: string;
}>[];

/**
 * Executive labels for the facet and control-layer postures (#4008).
 *
 * The status ENUMS stay exactly as they are — they are payload identity that specs,
 * the aggregator and the UI's `data-governance-status` attribute all pin. What changes
 * is that the API now publishes the reader-facing words next to them, so no surface has
 * to invent a label by upper-casing an enum. The judged capture rendered the enum
 * `not_yet_armed` as the badge `NOT YET ARMED` eighteen times.
 *
 * Nothing is suppressed: every posture keeps a label, and the "not supported" posture is
 * still named on the glass.
 */
export const VALUE_GOVERNANCE_FACET_STATUS_LABELS = {
  live: 'Connected',
  modeled: 'Modeled',
  not_yet_armed: 'Not yet supported by a connected record',
} as const satisfies Record<ValueGovernanceFacetStatus, string>;

export const VALUE_GOVERNANCE_LAYER_STATUS_LABELS = {
  armed: 'Ready',
  partially_armed: 'Partly ready',
  not_yet_armed: 'Not yet supported by a connected record',
} as const satisfies Record<ValueGovernanceLayerStatus, string>;

export const VALUE_GOVERNANCE_CONTROL_LAYER_DEFINITIONS = [
  {
    layerId: 'initiative_stand_up',
    label: 'Initiative stand-up',
    cadence: 'Weekly',
    owner: 'Lever owner',
    requiredOutput: 'Actions, milestone, operating KPI, risks, and evidence gaps',
    requiredFacets: ['source', 'owner'],
  },
  {
    layerId: 'value_certification',
    label: 'Value certification',
    cadence: 'Monthly',
    owner: 'CFO / FP&A',
    requiredOutput: 'Baseline, realized value, forecast, overlap, and P&L tie-out',
    requiredFacets: ['source', 'formula', 'owner', 'overlap_receipt', 'realized_p_and_l'],
  },
  {
    layerId: 'customer_carrier_guardrails',
    label: 'Customer / carrier guardrails',
    cadence: 'Monthly',
    owner: 'CCO / COO',
    requiredOutput: 'Retention, win rate, service, tender acceptance, and claims',
    requiredFacets: ['source', 'realized_p_and_l'],
  },
  {
    layerId: 'transformation_steering',
    label: 'Transformation steering',
    cadence: 'Monthly',
    owner: 'CEO / CFO / COO',
    requiredOutput: 'Decisions, escalations, funding, and scenario changes',
    requiredFacets: ['owner', 'overlap_receipt'],
  },
  {
    layerId: 'board_value_review',
    label: 'Board value review',
    cadence: 'Quarterly',
    owner: 'CEO / CFO',
    requiredOutput: 'Certified bridge, residual gap, risks, and next-wave approvals',
    requiredFacets: ['source', 'formula', 'owner', 'overlap_receipt', 'realized_p_and_l'],
  },
] as const satisfies readonly ValueGovernanceControlLayerDefinition[];

/**
 * Machine-readable values the panel intentionally publishes.
 *
 * The public workspace and SSE projections admit only this contract-owned
 * vocabulary. Shape-based snake-case admission remains forbidden: an
 * undeclared internal token is still opaqued.
 */
export const VALUE_GOVERNANCE_PANEL_CLOSED_ENUM_VALUES: readonly string[] = Object.freeze([
  ...new Set([
    VALUE_GOVERNANCE_PANEL_SCHEMA_VERSION,
    ...VALUE_GOVERNANCE_CATEGORY_IDS,
    ...VALUE_GOVERNANCE_FACET_IDS,
    ...VALUE_GOVERNANCE_FACET_STATUSES,
    ...VALUE_GOVERNANCE_LAYER_STATUSES,
    ...VALUE_GOVERNANCE_RECORD_TYPES,
    ...VALUE_GOVERNANCE_CONTROL_LAYER_IDS,
  ]),
]);

const VALUE_GOVERNANCE_PUBLIC_MACHINE_VALUES_BY_PATH: Readonly<
  Record<string, ReadonlySet<string>>
> = {
  schemaVersion: new Set([VALUE_GOVERNANCE_PANEL_SCHEMA_VERSION]),
  'controlLayers.layerId': new Set(VALUE_GOVERNANCE_CONTROL_LAYER_IDS),
  'controlLayers.status': new Set(VALUE_GOVERNANCE_LAYER_STATUSES),
  'controlLayers.recordBindings.recordType': new Set(
    VALUE_GOVERNANCE_RECORD_TYPES,
  ),
  'categories.categoryId': new Set(VALUE_GOVERNANCE_CATEGORY_IDS),
  'categories.facets.facetId': new Set(VALUE_GOVERNANCE_FACET_IDS),
  'categories.facets.status': new Set(VALUE_GOVERNANCE_FACET_STATUSES),
  'categories.facets.recordBindings.recordType': new Set(
    VALUE_GOVERNANCE_RECORD_TYPES,
  ),
};

/**
 * Slot-aware public admission for the panel's machine fields.
 *
 * Display labels, summaries, owners, cadence, and requirements are deliberately
 * absent. A closed-enum value placed in one of those fields still follows the
 * normal text/redaction path.
 */
export function isValueGovernancePanelPublicMachineValue(
  path: readonly string[],
  value: string,
): boolean {
  const values =
    VALUE_GOVERNANCE_PUBLIC_MACHINE_VALUES_BY_PATH[path.join('.')];
  return Boolean(values?.has(value));
}

export type ValueGovernanceControlLayer = Readonly<{
  layerId: ValueGovernanceControlLayerId;
  label: string;
  cadence: string;
  owner: string;
  requiredOutput: string;
  status: ValueGovernanceLayerStatus;
  /** Reader-facing words for `status`; never derived by upper-casing the enum. */
  statusLabel: string;
  summary: string;
  recordBindings: readonly ValueGovernanceRecordBinding[];
  missingRequirements: readonly string[];
}>;

export type ValueGovernancePanel = Readonly<{
  schemaVersion: typeof VALUE_GOVERNANCE_PANEL_SCHEMA_VERSION;
  title: 'Value governance and control plan';
  headline: typeof VALUE_GOVERNANCE_PANEL_HEADLINE;
  takeaway: typeof VALUE_GOVERNANCE_PANEL_TAKEAWAY;
  controlLayers: readonly ValueGovernanceControlLayer[];
  categories: readonly ValueGovernanceCategory[];
  realizationRule: typeof VALUE_GOVERNANCE_REALIZATION_RULE;
}>;
